/**
 * useNotifications — React Query hooks over the existing notificationService.
 *
 * Why React Query alongside the legacy notificationStore?
 *   - Polling: refetch every 15s while tab is focused (real-time-enough until
 *     Socket.io is wired in Phase C2)
 *   - Automatic dedup + caching
 *   - Optimistic updates on mark-as-read
 *   - Easy to swap the polling for Socket.io push later — just kill the
 *     refetchInterval, the rest of the contract stays the same
 *
 * The existing Zustand notificationStore is NOT removed; both can coexist
 * until we migrate consumers. New code should use these hooks.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  notificationService,
  type Notification,
  type NotificationResponse,
} from "@/services/notificationService";

// ─── Query keys ────────────────────────────────────────────────────

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (filters: { limit?: number; unreadOnly?: boolean }) =>
    [...notificationKeys.all, "list", filters] as const,
};

// ─── Hooks ─────────────────────────────────────────────────────────

/**
 * Main hook: fetch user's notifications. Polls every 15s while tab focused.
 *
 * @param params.limit Default 20 — small enough to feel snappy, large enough
 *   to cover most needs without scrolling
 * @param params.unreadOnly Filter to unread only (e.g. for badge counts)
 */
export function useNotifications(
  params: { limit?: number; unreadOnly?: boolean } = {},
) {
  const normalized = {
    limit: params.limit ?? 20,
    unreadOnly: params.unreadOnly ?? false,
  };
  return useQuery<NotificationResponse>({
    queryKey: notificationKeys.list(normalized),
    queryFn: () => notificationService.getNotifications(normalized),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    refetchInterval: 15_000, // poll every 15s — replace with Socket.io in C2
    refetchIntervalInBackground: false, // pause when tab hidden
  });
}

/**
 * Mark a single notification as read (optimistic).
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<Notification, Error, string>({
    mutationFn: (id) => notificationService.markAsRead(id),
    // Optimistic: flip read=true immediately, rollback on error
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: notificationKeys.all });
      const snapshots = qc.getQueriesData<NotificationResponse>({
        queryKey: notificationKeys.all,
      });
      for (const [key, value] of snapshots) {
        if (!value) continue;
        qc.setQueryData<NotificationResponse>(key, {
          ...value,
          notifications: value.notifications.map((n) =>
            n._id === id ? { ...n, read: true } : n,
          ),
          unread: Math.max(0, value.unread - 1),
        });
      }
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      // Rollback
      const snaps = (
        ctx as
          | {
              snapshots?: Array<
                [readonly unknown[], NotificationResponse | undefined]
              >;
            }
          | undefined
      )?.snapshots;
      if (snaps) {
        for (const [key, value] of snaps) {
          qc.setQueryData(key, value);
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark all notifications as read (single mutation, full invalidation).
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      // Optimistic flip everything to read
      const snapshots = qc.getQueriesData<NotificationResponse>({
        queryKey: notificationKeys.all,
      });
      for (const [key, value] of snapshots) {
        if (!value) continue;
        qc.setQueryData<NotificationResponse>(key, {
          ...value,
          notifications: value.notifications.map((n) => ({ ...n, read: true })),
          unread: 0,
        });
      }
      // Then refetch to be sure
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Delete a notification.
 */
export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => notificationService.deleteNotification(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
